/** Logo splash after sign-in only. Refresh uses the in-app Loader. */

const BOOT_BG = '#ffffff';
export const LOGO_SPLASH_KEY = 'lf_logo_splash';

let bootFinished = false;
let dismissTimer: number | null = null;
let contentPollTimer: number | null = null;
const bootFinishedListeners = new Set<() => void>();
const bootStartedListeners = new Set<() => void>();

const applyBootBackground = () => {
  document.documentElement.style.background = BOOT_BG;
  document.body.style.background = BOOT_BG;
  const root = document.getElementById('root');
  if (root) root.style.background = BOOT_BG;
};

const clearBootBackground = () => {
  document.documentElement.style.background = '';
  document.body.style.background = '';
  const root = document.getElementById('root');
  if (root) root.style.background = '';
  document.documentElement.classList.remove('lf-booting');
  document.body.classList.remove('lf-booting');
};

export const isBootSplashFinished = () => bootFinished;

export const shouldUseLogoSplash = () => sessionStorage.getItem(LOGO_SPLASH_KEY) === '1';

/** Call on successful sign-in so the next app boot shows the logo splash. */
export const markLogoSplashForSignIn = () => {
  sessionStorage.setItem(LOGO_SPLASH_KEY, '1');
  bootFinished = false;
};

export const subscribeBootSplashFinished = (listener: () => void) => {
  bootFinishedListeners.add(listener);
  return () => {
    bootFinishedListeners.delete(listener);
  };
};

/** Sign-in can start the splash after first paint — Chatwoot needs this to stay visible. */
export const subscribeBootSplashStarted = (listener: () => void) => {
  bootStartedListeners.add(listener);
  return () => {
    bootStartedListeners.delete(listener);
  };
};

const notifyBootFinished = () => {
  bootFinishedListeners.forEach((listener) => listener());
};

const notifyBootStarted = () => {
  bootStartedListeners.forEach((listener) => listener());
};

const removeSplashElement = () => {
  const el = document.getElementById('boot-splash');
  if (el) el.remove();
};

/** Refresh / cold open: no logo splash. Sign-in flag: show logo splash. */
export const initPageBoot = () => {
  const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  const isReload = nav?.type === 'reload'
    || (typeof performance !== 'undefined' && (performance as any).navigation?.type === 1);

  if (isReload || !shouldUseLogoSplash()) {
    sessionStorage.removeItem(LOGO_SPLASH_KEY);
    removeSplashElement();
    clearBootBackground();
    bootFinished = true;
    notifyBootFinished();
    return;
  }
  ensureHtmlBootSplash();
};

export const ensureHtmlBootSplash = () => {
  if (bootFinished || !shouldUseLogoSplash()) return;

  if (dismissTimer != null) {
    window.clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  if (contentPollTimer != null) {
    window.clearInterval(contentPollTimer);
    contentPollTimer = null;
  }
  document.documentElement.classList.add('lf-booting');
  document.body.classList.add('lf-booting');
  applyBootBackground();
  const el = document.getElementById('boot-splash');
  if (el) {
    el.dataset.dismissing = '';
    el.style.opacity = '1';
    el.style.pointerEvents = 'auto';
    el.style.transition = '';
    notifyBootStarted();
    return;
  }
  const root = document.createElement('div');
  root.id = 'boot-splash';
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');
  root.setAttribute('aria-label', 'Loading Tripple');
  root.innerHTML = `
    <div class="boot-stack">
      <img src="${process.env.PUBLIC_URL || ''}/logo.png" alt="Tripple" />
    </div>
  `;
  document.body.appendChild(root);
  notifyBootStarted();
};

const hasPaintedAppContent = () => {
  const main = document.getElementById('main_cont');
  if (!main) return false;
  const rect = main.getBoundingClientRect();
  if (rect.width < 40 || rect.height < 40) return false;
  if (main.querySelector('table, canvas, input, textarea, select, button, a, img, svg')) {
    return true;
  }
  const text = (main.innerText || '').replace(/\s+/g, ' ').trim();
  return text.length > 24;
};

export const dismissHtmlBootSplash = () => {
  const alreadyDone = bootFinished;
  bootFinished = true;
  sessionStorage.removeItem(LOGO_SPLASH_KEY);
  if (dismissTimer != null) {
    window.clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  if (contentPollTimer != null) {
    window.clearInterval(contentPollTimer);
    contentPollTimer = null;
  }
  clearBootBackground();
  const el = document.getElementById('boot-splash');
  if (el) {
    if (el.dataset.dismissing === '1') {
      el.remove();
    } else {
      el.dataset.dismissing = '1';
      el.style.transition = 'opacity 0.15s ease-out';
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
      dismissTimer = window.setTimeout(() => {
        dismissTimer = null;
        el.remove();
      }, 150);
    }
  }
  if (!alreadyDone) notifyBootFinished();
};

export const dismissHtmlBootSplashWhenContentReady = () => {
  if (bootFinished) return;
  if (!shouldUseLogoSplash()) {
    dismissHtmlBootSplash();
    return;
  }

  if (contentPollTimer != null) {
    window.clearInterval(contentPollTimer);
    contentPollTimer = null;
  }

  const finish = () => dismissHtmlBootSplash();

  if (hasPaintedAppContent()) {
    finish();
    return;
  }

  const started = Date.now();
  contentPollTimer = window.setInterval(() => {
    if (bootFinished) {
      if (contentPollTimer != null) {
        window.clearInterval(contentPollTimer);
        contentPollTimer = null;
      }
      return;
    }
    if (hasPaintedAppContent() || Date.now() - started > 4000) {
      if (contentPollTimer != null) {
        window.clearInterval(contentPollTimer);
        contentPollTimer = null;
      }
      finish();
    }
  }, 32);
};
